import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, Check, Loader2, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../components/ui/dialog";
import { useAuth } from "../context/AuthContext";
import { INTRO_OFFER_REMINDER_EVENT } from "../lib/introOffer";
import { INTRO_OFFER } from "../lib/plans";

const SEEN_KEY = "vox_intro_offer_seen";
const REMINDER_SEEN_KEY = "vox_intro_offer_reminder_seen";
const REMINDER_DELAY_MS = 6000;

export default function IntroOfferModal() {
  const location = useLocation();
  const {
    loading,
    isPaid,
    authOpen,
    pricingOpen,
    requestCheckout,
  } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reminderPending, setReminderPending] = useState(false);

  useEffect(() => {
    const isOfferPage = location.pathname === "/" || location.pathname === "/studio";
    if (
      loading ||
      isPaid ||
      authOpen ||
      pricingOpen ||
      !isOfferPage ||
      sessionStorage.getItem(SEEN_KEY)
    ) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      sessionStorage.setItem(SEEN_KEY, "true");
      setOpen(true);
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [authOpen, isPaid, loading, location.pathname, pricingOpen]);

  useEffect(() => {
    const queueReminder = () => {
      if (!isPaid && !sessionStorage.getItem(REMINDER_SEEN_KEY)) {
        setReminderPending(true);
      }
    };

    window.addEventListener(INTRO_OFFER_REMINDER_EVENT, queueReminder);
    return () => window.removeEventListener(INTRO_OFFER_REMINDER_EVENT, queueReminder);
  }, [isPaid]);

  useEffect(() => {
    if (
      !reminderPending ||
      loading ||
      isPaid ||
      authOpen ||
      pricingOpen ||
      open ||
      location.pathname !== "/studio" ||
      sessionStorage.getItem(REMINDER_SEEN_KEY)
    ) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      sessionStorage.setItem(REMINDER_SEEN_KEY, "true");
      setReminderPending(false);
      setOpen(true);
    }, REMINDER_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [authOpen, isPaid, loading, location.pathname, open, pricingOpen, reminderPending]);

  useEffect(() => {
    if (isPaid) {
      setOpen(false);
      setReminderPending(false);
    }
  }, [isPaid]);

  const startOffer = async () => {
    setBusy(true);
    setOpen(false);
    try {
      await requestCheckout(INTRO_OFFER.key);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="intro-offer-modal"
        className="rounded-none bg-[#080a03] border border-volt text-white max-w-lg max-h-[92vh] p-0 gap-0 font-mono overflow-y-auto shadow-[0_0_80px_rgba(212,255,0,0.18)]"
      >
        <div className="h-1.5 bg-volt" />
        <div className="relative p-6 sm:p-9">
          <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-volt/10 blur-3xl" aria-hidden />

          <div className="relative">
            <div className="inline-flex items-center gap-2 border border-volt/40 bg-volt/5 px-3 py-1.5 mb-6 text-[10px] font-bold uppercase tracking-[0.24em] text-volt">
              <Sparkles size={13} /> Limited intro offer
            </div>

            <DialogTitle className="font-display text-4xl sm:text-5xl leading-[0.95] tracking-tight font-normal mb-4">
              YOUR FIRST 7 DAYS.
              <br />
              <span className="text-volt">ONE DOLLAR.</span>
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-400 leading-relaxed mb-7 max-w-md">
              Start Weekly Unlimited and get both voice engines, unlimited characters,
              and unlimited WAV exports from day one.
            </DialogDescription>

            <div className="grid grid-cols-[auto_1fr] items-center gap-x-5 border-y border-zinc-800 py-5 mb-6">
              <div className="font-display text-6xl leading-none text-volt">{INTRO_OFFER.firstPrice}</div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-white">
                  {INTRO_OFFER.firstDuration}
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  Then {INTRO_OFFER.renewalPrice}
                </div>
              </div>
            </div>

            <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-3 mb-7">
              {["Unlimited generations", "High + low quality", "Unlimited WAV exports", "Cancel anytime"].map((perk) => (
                <li key={perk} className="flex items-center gap-2 text-xs text-zinc-300">
                  <Check size={14} className="text-volt shrink-0" strokeWidth={3} />
                  {perk}
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={startOffer}
              disabled={busy}
              data-testid="intro-offer-cta"
              className="w-full flex items-center justify-center gap-2 bg-volt text-black py-4 text-sm font-bold uppercase tracking-widest hover:bg-volt-hover transition-colors duration-100 disabled:opacity-60"
            >
              {busy ? <Loader2 size={16} className="animate-spin" /> : <Zap size={16} fill="currentColor" />}
              Start for $1
              {!busy && <ArrowRight size={16} strokeWidth={2.5} />}
            </button>

            <p className="text-[10px] text-zinc-500 text-center leading-relaxed mt-4">
              By continuing, you start a recurring subscription. $1 is charged for the first 7 days,
              then $5.99 every 7 days until canceled. Manage or cancel anytime in Billing.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
